import { useState, useId, useEffect } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import SEO from "@/components/SEO";
import { submitContactForm, fetchSocialLinks, formatApiError } from "@/lib/api";
import {
  EnvelopeSimple,
  PaperPlaneTilt,
  ArrowRight,
  CheckCircle,
  TwitterLogo,
  LinkedinLogo,
  GithubLogo,
  YoutubeLogo,
  InstagramLogo,
  TelegramLogo,
  Clock,
  ChatCircleDots,
  WarningCircle,
  SpinnerGap,
  ShareNetwork,
} from "@phosphor-icons/react";

// ─── Constants ────────────────────────────────────────────────────────────────

const SUPPORT_EMAIL  = "support@trendming.com";
const RESPONSE_HOURS = "24 hours";
const SITE_URL       = "https://trendming.com";

// Platform → icon + color for dynamic social links
const PLATFORM_META = {
  twitter:   { icon: TwitterLogo,   color: "hover:text-sky-500",  label: "Twitter / X" },
  linkedin:  { icon: LinkedinLogo,  color: "hover:text-blue-600", label: "LinkedIn" },
  youtube:   { icon: YoutubeLogo,   color: "hover:text-red-500",  label: "YouTube" },
  instagram: { icon: InstagramLogo, color: "hover:text-pink-500", label: "Instagram" },
  github:    { icon: GithubLogo,    color: "hover:text-slate-900", label: "GitHub" },
  telegram:  { icon: TelegramLogo,  color: "hover:text-sky-500",  label: "Telegram" },
};

const CONTACT_CHANNELS = [
  {
    icon: ChatCircleDots,
    label: "Product support",
    value: SUPPORT_EMAIL,
    href: `mailto:${SUPPORT_EMAIL}`,
  },
  {
    icon: Clock,
    label: "Response time",
    value: `Within ${RESPONSE_HOURS}`,
    href: null,
  },
];

const SUBJECT_OPTIONS = [
  "General enquiry",
  "Product feedback",
  "Bug report",
  "Press & media",
  "Partnership & business",
  "Billing & subscription",
  "Privacy & legal",
  "Other",
];

// ─── Structured data ──────────────────────────────────────────────────────────

const CONTACT_SCHEMA = {
  "@context": "https://schema.org",
  "@type": "ContactPage",
  name: "Contact TrendMing",
  description:
    "Get in touch with the TrendMing team for product support, partnerships, press, or general enquiries.",
  url: `${SITE_URL}/contact`,
  isPartOf: { "@type": "WebSite", name: "TrendMing", url: SITE_URL },
};

// ─── Validation ───────────────────────────────────────────────────────────────

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

function validate(fields) {
  const errors = {};
  if (!fields.name.trim() || fields.name.trim().length < 2)
    errors.name = "Please enter your full name (at least 2 characters).";
  if (!fields.email.trim() || !EMAIL_RE.test(fields.email.trim()))
    errors.email = "Please enter a valid email address.";
  if (!fields.subject)
    errors.subject = "Please select a subject.";
  if (!fields.message.trim() || fields.message.trim().length < 10)
    errors.message = "Message must be at least 10 characters.";
  if (fields.message.trim().length > 3000)
    errors.message = "Message must not exceed 3000 characters.";
  return errors;
}

// ─── Reusable form field primitives ──────────────────────────────────────────

const inputBase = "w-full bg-slate-50 border px-4 py-3 text-slate-900 text-sm placeholder-slate-400 outline-none transition-colors focus:ring-2 focus:ring-blue-500/30 rounded-lg";

function FieldLabel({ htmlFor, children, required }) {
  return (
    <label
      htmlFor={htmlFor}
      className="block text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2"
    >
      {children}
      {required && (
        <span className="text-blue-600 ml-1" aria-hidden="true">*</span>
      )}
    </label>
  );
}

function FieldError({ id, message }) {
  if (!message) return null;
  return (
    <p id={id} role="alert" className="flex items-center gap-1.5 mt-2 text-xs text-red-500">
      <WarningCircle size={12} weight="fill" aria-hidden="true" />
      {message}
    </p>
  );
}

function InputField({ id, label, type = "text", value, onChange, onBlur, error, placeholder, required, autoComplete }) {
  const errorId = `${id}-error`;
  return (
    <div>
      <FieldLabel htmlFor={id} required={required}>{label}</FieldLabel>
      <input
        id={id}
        type={type}
        value={value}
        onChange={onChange}
        onBlur={onBlur}
        placeholder={placeholder}
        autoComplete={autoComplete}
        required={required}
        aria-describedby={error ? errorId : undefined}
        aria-invalid={!!error}
        className={`${inputBase} ${
          error ? "border-red-300 focus:border-red-400" : "border-slate-200 hover:border-slate-300 focus:border-blue-500"
        }`}
      />
      <FieldError id={errorId} message={error} />
    </div>
  );
}

function SelectField({ id, label, value, onChange, onBlur, error, options, required }) {
  const errorId = `${id}-error`;
  return (
    <div>
      <FieldLabel htmlFor={id} required={required}>{label}</FieldLabel>
      <select
        id={id}
        value={value}
        onChange={onChange}
        onBlur={onBlur}
        required={required}
        aria-describedby={error ? errorId : undefined}
        aria-invalid={!!error}
        className={`${inputBase} appearance-none ${
          value ? "text-slate-900" : "text-slate-400"
        } ${error ? "border-red-300 focus:border-red-400" : "border-slate-200 hover:border-slate-300 focus:border-blue-500"}`}
      >
        <option value="" disabled>Select a subject…</option>
        {options.map((opt) => (
          <option key={opt} value={opt}>{opt}</option>
        ))}
      </select>
      <FieldError id={errorId} message={error} />
    </div>
  );
}

function TextareaField({ id, label, value, onChange, onBlur, error, placeholder, required, maxLength }) {
  const errorId = `${id}-error`;
  const remaining = maxLength - value.length;
  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <FieldLabel htmlFor={id} required={required}>{label}</FieldLabel>
        <span
          className={`text-[10px] tabular-nums ${
            remaining < 200 ? "text-yellow-500" : "text-slate-400"
          }`}
          aria-live="polite"
        >
          {remaining.toLocaleString()} left
        </span>
      </div>
      <textarea
        id={id}
        value={value}
        onChange={onChange}
        onBlur={onBlur}
        placeholder={placeholder}
        required={required}
        maxLength={maxLength}
        rows={6}
        aria-describedby={error ? errorId : undefined}
        aria-invalid={!!error}
        className={`${inputBase} resize-y min-h-[140px] ${
          error ? "border-red-300 focus:border-red-400" : "border-slate-200 hover:border-slate-300 focus:border-blue-500"
        }`}
      />
      <FieldError id={errorId} message={error} />
    </div>
  );
}

// ─── Success state ────────────────────────────────────────────────────────────

function SuccessPanel({ onReset }) {
  return (
    <div className="flex flex-col items-center text-center py-16 px-8 border border-slate-200 bg-slate-50 rounded-xl">
      <div className="w-14 h-14 bg-green-50 border border-green-200 flex items-center justify-center mb-6 rounded-full">
        <CheckCircle size={28} weight="duotone" className="text-green-500" />
      </div>
      <h2 className="font-bold text-2xl md:text-3xl mb-3 text-slate-900">
        Message received
      </h2>
      <p className="text-slate-500 text-base leading-relaxed max-w-sm mb-8">
        Thanks for reaching out. We'll review your message and reply to{" "}
        <span className="text-slate-900 font-medium">your email</span> within{" "}
        {RESPONSE_HOURS}.
      </p>
      <button
        onClick={onReset}
        className="inline-flex items-center gap-2 border border-slate-200 hover:bg-slate-100 text-slate-700 font-bold py-3 px-6 text-sm rounded-lg transition-colors"
      >
        Send another message
      </button>
    </div>
  );
}

// ─── Page ─────────────────────────────────────────────────────────────────────

const INITIAL_FORM = { name: "", email: "", subject: "", message: "" };

export default function ContactPage() {
  const formId = useId();
  const [form, setForm]     = useState(INITIAL_FORM);
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [status, setStatus] = useState("idle"); // idle | loading | success
  const [socialLinks, setSocialLinks] = useState([]);

  useEffect(() => {
    fetchSocialLinks()
      .then((data) => setSocialLinks(Array.isArray(data) ? data : []))
      .catch(() => {});
  }, []);

  // ── Field helpers ──
  const field = (key) => ({
    id:       `${formId}-${key}`,
    value:    form[key],
    error:    touched[key] ? errors[key] : undefined,
    onChange: (e) => {
      const val = e.target.value;
      setForm((f) => ({ ...f, [key]: val }));
      if (touched[key]) {
        const next = { ...form, [key]: val };
        setErrors(validate(next));
      }
    },
    onBlur: () => {
      setTouched((t) => ({ ...t, [key]: true }));
      setErrors(validate(form));
    },
  });

  // ── Submit ──
  const handleSubmit = async (e) => {
    e.preventDefault();
    setTouched({ name: true, email: true, subject: true, message: true });
    const errs = validate(form);
    setErrors(errs);
    if (Object.keys(errs).length > 0) {
      const firstErrId = `${formId}-${Object.keys(errs)[0]}`;
      document.getElementById(firstErrId)?.focus();
      return;
    }

    setStatus("loading");
    try {
      await submitContactForm({
        name:    form.name.trim(),
        email:   form.email.trim(),
        subject: form.subject,
        message: form.message.trim(),
      });
      setStatus("success");
      toast.success("Message sent! We'll be in touch shortly.");
    } catch (err) {
      setStatus("idle");
      toast.error(formatApiError(err));
    }
  };

  const handleReset = () => {
    setForm(INITIAL_FORM);
    setErrors({});
    setTouched({});
    setStatus("idle");
  };

  return (
    <>
      <SEO
        title="Contact Us | TrendMing"
        description="Get in touch with the TrendMing team. Reach us for product support, partnerships, press enquiries, billing, or general feedback. We respond within 24 hours."
        canonical={`${SITE_URL}/contact`}
        ogType="website"
        structuredData={[CONTACT_SCHEMA]}
      />

      <main className="max-w-[1200px] mx-auto px-4 md:px-8 py-12 md:py-20">

        {/* ── Page header ────────────────────────────────────────────────── */}
        <header className="mb-12 md:mb-16">
          <span className="text-xs font-semibold uppercase tracking-wider text-blue-600">
            / Contact
          </span>
          <h1 className="font-bold text-4xl md:text-6xl mt-4 mb-5 leading-[1.05] text-slate-900">
            Let's talk.
          </h1>
          <p className="text-slate-600 text-lg md:text-xl max-w-2xl leading-relaxed">
            Have a question, idea, or issue? Fill in the form below and a member of
            our team will reply within{" "}
            <span className="text-slate-900 font-semibold">{RESPONSE_HOURS}</span>.
          </p>
        </header>

        {/* ── Two-column body ─────────────────────────────────────────────── */}
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_380px] gap-12 lg:gap-16 items-start">

          {/* ── Contact form ──────────────────────────────────────────────── */}
          <section aria-labelledby="form-heading">
            <h2 id="form-heading" className="sr-only">Contact form</h2>

            {status === "success" ? (
              <SuccessPanel onReset={handleReset} />
            ) : (
              <form
                onSubmit={handleSubmit}
                noValidate
                aria-label="Contact form"
                className="space-y-6"
              >
                {/* Row: name + email */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                  <InputField
                    {...field("name")}
                    label="Full name"
                    placeholder="Jane Smith"
                    autoComplete="name"
                    required
                  />
                  <InputField
                    {...field("email")}
                    label="Email address"
                    type="email"
                    placeholder="jane@example.com"
                    autoComplete="email"
                    required
                  />
                </div>

                {/* Subject */}
                <SelectField
                  {...field("subject")}
                  label="Subject"
                  options={SUBJECT_OPTIONS}
                  required
                />

                {/* Message */}
                <TextareaField
                  {...field("message")}
                  label="Message"
                  placeholder="Tell us what's on your mind…"
                  required
                  maxLength={3000}
                />

                {/* Submit */}
                <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 pt-2">
                  <button
                    type="submit"
                    disabled={status === "loading"}
                    className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-60 disabled:cursor-not-allowed text-white font-bold py-3.5 px-8 text-sm rounded-lg transition-colors"
                    aria-label={status === "loading" ? "Sending message…" : "Send message"}
                  >
                    {status === "loading" ? (
                      <>
                        <SpinnerGap size={16} weight="bold" className="animate-spin" aria-hidden="true" />
                        Sending…
                      </>
                    ) : (
                      <>
                        <PaperPlaneTilt size={16} weight="bold" aria-hidden="true" />
                        Send message
                      </>
                    )}
                  </button>
                  <p className="text-slate-400 text-xs leading-relaxed">
                    We typically respond within {RESPONSE_HOURS}.<br className="hidden sm:block" />
                    No spam, ever.
                  </p>
                </div>

                {/* General error summary */}
                {Object.keys(touched).length > 0 && Object.keys(errors).length > 0 && (
                  <div
                    role="alert"
                    className="border border-red-200 bg-red-50 px-4 py-3 flex items-start gap-3 rounded-lg"
                  >
                    <WarningCircle size={16} weight="fill" className="text-red-500 mt-0.5 shrink-0" aria-hidden="true" />
                    <p className="text-red-600 text-sm">
                      Please fix the highlighted fields before sending.
                    </p>
                  </div>
                )}
              </form>
            )}
          </section>

          {/* ── Sidebar: contact info + socials ───────────────────────────── */}
          <aside className="space-y-8">

            {/* Contact channels */}
            <div className="border border-slate-200 divide-y divide-slate-200 rounded-xl overflow-hidden">
              <div className="px-6 py-4 bg-slate-50">
                <span className="text-xs font-semibold uppercase tracking-wider text-slate-500">
                  Direct contact
                </span>
              </div>
              {CONTACT_CHANNELS.map(({ icon: Icon, label, value, href }) => (
                <div key={label} className="flex items-start gap-4 px-6 py-4 bg-white hover:bg-slate-50 transition-colors group">
                  <Icon
                    size={18}
                    weight="duotone"
                    className="text-blue-600 shrink-0 mt-0.5"
                    aria-hidden="true"
                  />
                  <div className="min-w-0">
                    <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-400 mb-0.5">
                      {label}
                    </p>
                    {href ? (
                      <a
                        href={href}
                        className="text-sm text-slate-600 hover:text-slate-900 transition-colors truncate block"
                      >
                        {value}
                      </a>
                    ) : (
                      <p className="text-sm text-slate-700">{value}</p>
                    )}
                  </div>
                </div>
              ))}
            </div>

            {/* Social links — only rendered when at least one is active */}
            {socialLinks.length > 0 && (
              <div className="border border-slate-200 divide-y divide-slate-200 rounded-xl overflow-hidden">
                <div className="px-6 py-4 bg-slate-50">
                  <span className="text-xs font-semibold uppercase tracking-wider text-slate-500">
                    Follow us
                  </span>
                </div>
                {socialLinks.map(({ platform, url, handle }) => {
                  const meta = PLATFORM_META[platform] || {};
                  const Icon = meta.icon || ShareNetwork;
                  const color = meta.color || "hover:text-slate-900";
                  const displayLabel = meta.label || platform;
                  return (
                    <a
                      key={platform}
                      href={url}
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label={`${displayLabel}${handle ? `: ${handle}` : ""}`}
                      className={`flex items-center gap-4 px-6 py-4 bg-white hover:bg-slate-50 transition-colors group text-slate-500 ${color}`}
                    >
                      <Icon
                        size={18}
                        weight="duotone"
                        className="shrink-0 transition-colors"
                        aria-hidden="true"
                      />
                      <div className="flex-1 min-w-0">
                        <span className="text-sm font-semibold text-slate-700 group-hover:text-slate-900 transition-colors block">
                          {displayLabel}
                        </span>
                        {handle && (
                          <span className="text-[11px] text-slate-400">{handle}</span>
                        )}
                      </div>
                      <ArrowRight
                        size={13}
                        weight="bold"
                        className="opacity-0 group-hover:opacity-100 transition-opacity shrink-0"
                        aria-hidden="true"
                      />
                    </a>
                  );
                })}
              </div>
            )}

            {/* Quick links */}
            <div className="border border-slate-200 bg-white px-6 py-6 space-y-3 rounded-xl">
              <p className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-4">
                Before you write
              </p>
              {[
                { label: "Browse our FAQ", to: "/pricing", sub: "Plans & billing questions" },
                { label: "View Terms & Conditions", to: "/terms", sub: "Legal & usage policy" },
                { label: "About TrendMing", to: "/about", sub: "Our mission and team" },
              ].map(({ label, to, sub }) => (
                <Link
                  key={to}
                  to={to}
                  className="flex items-center justify-between group py-2 border-b border-slate-100 last:border-0"
                >
                  <div>
                    <span className="text-sm text-slate-700 group-hover:text-slate-900 transition-colors block">
                      {label}
                    </span>
                    <span className="text-[10px] text-slate-400">{sub}</span>
                  </div>
                  <ArrowRight
                    size={13}
                    weight="bold"
                    className="text-slate-400 group-hover:text-blue-600 transition-colors shrink-0 ml-4"
                    aria-hidden="true"
                  />
                </Link>
              ))}
            </div>

          </aside>
        </div>
      </main>
    </>
  );
}
